import { theme } from 'antd';
import type { GlobalToken } from 'antd';
import styled from 'styled-components';
import {
  InboxOutlined,
  FileSearchOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import { Button } from '../Button';
import { radius } from '../../theme/radius';

export type EmptyStateVariant = 'default' | 'search' | 'folder';
export type EmptyStateSize = 'small' | 'medium' | 'large';

export interface EmptyStateAction {
  /** Button label */
  label: string;
  /** Click handler */
  onClick: () => void;
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'tertiary';
}

export interface EmptyStateProps {
  /** Visual variant with different default icons */
  variant?: EmptyStateVariant;
  /** Custom icon (overrides variant icon) */
  icon?: React.ReactNode;
  /** Title text */
  title: string;
  /** Description text */
  description?: string;
  /** Primary action button */
  action?: EmptyStateAction;
  /** Secondary action button */
  secondaryAction?: EmptyStateAction;
  /** Size of the empty state */
  size?: EmptyStateSize;
}

interface TokenProps {
  $token: GlobalToken;
}

// Size config using token-based values
const getSizeConfig = (token: GlobalToken) => ({
  small: {
    iconSize: 40,
    titleSize: token.fontSize,
    descSize: token.fontSizeSM,
    padding: token.paddingLG,
    gap: token.marginSM,
  },
  medium: {
    iconSize: 64,
    titleSize: token.fontSizeLG,
    descSize: token.fontSize,
    padding: token.paddingLG * 2,
    gap: token.margin,
  },
  large: {
    iconSize: 80,
    titleSize: token.fontSizeHeading4,
    descSize: token.fontSizeLG,
    padding: token.paddingLG * 3,
    gap: token.marginLG,
  },
});

const variantIcons = {
  default: InboxOutlined,
  search: FileSearchOutlined,
  folder: FolderOpenOutlined,
};

const Wrapper = styled.div<
  TokenProps & {
    $size: EmptyStateSize;
    $sizeConfig: ReturnType<typeof getSizeConfig>;
  }
>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${({ $sizeConfig, $size }) => $sizeConfig[$size].padding}px;
  gap: ${({ $sizeConfig, $size }) => $sizeConfig[$size].gap}px;
`;

const IconWrapper = styled.div<
  TokenProps & {
    $size: EmptyStateSize;
    $sizeConfig: ReturnType<typeof getSizeConfig>;
  }
>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $sizeConfig, $size }) => $sizeConfig[$size].iconSize * 1.5}px;
  height: ${({ $sizeConfig, $size }) => $sizeConfig[$size].iconSize * 1.5}px;
  border-radius: ${radius.circle};
  background: ${({ $token }) => $token.colorFillTertiary};
  color: ${({ $token }) => $token.colorTextQuaternary};

  .anticon {
    font-size: ${({ $sizeConfig, $size }) => $sizeConfig[$size].iconSize}px;
  }

  svg {
    width: ${({ $sizeConfig, $size }) => $sizeConfig[$size].iconSize}px;
    height: ${({ $sizeConfig, $size }) => $sizeConfig[$size].iconSize}px;
  }
`;

const Title = styled.h3<
  TokenProps & {
    $size: EmptyStateSize;
    $sizeConfig: ReturnType<typeof getSizeConfig>;
  }
>`
  margin: 0;
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $sizeConfig, $size }) => $sizeConfig[$size].titleSize}px;
  font-weight: ${({ $token }) => $token.fontWeightStrong};
  color: ${({ $token }) => $token.colorText};
`;

const Description = styled.p<
  TokenProps & {
    $size: EmptyStateSize;
    $sizeConfig: ReturnType<typeof getSizeConfig>;
  }
>`
  margin: 0;
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $sizeConfig, $size }) => $sizeConfig[$size].descSize}px;
  font-weight: ${({ $token }) => $token.fontWeightStrong - 100};
  color: ${({ $token }) => $token.colorTextSecondary};
  max-width: 400px;
  line-height: ${({ $token }) => $token.lineHeight};
`;

const ActionsWrapper = styled.div<TokenProps>`
  display: flex;
  gap: ${({ $token }) => $token.marginSM}px;
  margin-top: ${({ $token }) => $token.marginXS}px;
`;

/**
 * EmptyState component based on Libertify Design System
 *
 * Used to display a placeholder when there's no data to show.
 *
 * @example
 * ```tsx
 * import { EmptyState } from '@libertify/ui';
 *
 * // Basic empty state
 * <EmptyState
 *   title="No documents yet"
 *   description="Upload your first document to get started"
 *   action={{
 *     label: "Upload Document",
 *     onClick: () => console.log('upload'),
 *   }}
 * />
 *
 * // Search empty state
 * <EmptyState
 *   variant="search"
 *   title="No results found"
 *   description="Try adjusting your search terms"
 * />
 * ```
 */
export const EmptyState = ({
  variant = 'default',
  icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'medium',
}: EmptyStateProps) => {
  const { token } = theme.useToken();
  const sizeConfig = getSizeConfig(token);
  const IconComponent = variantIcons[variant];

  return (
    <Wrapper $token={token} $size={size} $sizeConfig={sizeConfig}>
      <IconWrapper $token={token} $size={size} $sizeConfig={sizeConfig}>
        {icon || <IconComponent />}
      </IconWrapper>
      <Title $token={token} $size={size} $sizeConfig={sizeConfig}>
        {title}
      </Title>
      {description && (
        <Description $token={token} $size={size} $sizeConfig={sizeConfig}>
          {description}
        </Description>
      )}
      {(action || secondaryAction) && (
        <ActionsWrapper $token={token}>
          {action && (
            <Button
              variant={action.variant || 'primary'}
              size={size === 'small' ? 's' : 'm'}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant={secondaryAction.variant || 'tertiary'}
              size={size === 'small' ? 's' : 'm'}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
        </ActionsWrapper>
      )}
    </Wrapper>
  );
};

export default EmptyState;
